import React from "react";
import { useTranslation } from "react-i18next";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import ActionMenu from "@/components/ActionMenu";
import { useUserStore } from "@/hooks/useUserStore";
import { Theme } from "@mui/material";
import { usePermissionAlert } from "@/hooks/usePermissionAlert";
import { Box } from "@mui/material";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";

interface ProfileMenuProps {
  handleOpenSupportStaffModal: () => void;
  handleOpenManualReimbursementModal: () => void;
  handleOpenDismissModal: () => void;
  theme: Theme;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  handleOpenSupportStaffModal,
  handleOpenDismissModal,
  handleOpenManualReimbursementModal,
  theme,
}) => {
  const { t } = useTranslation("clients");
  const { userHasAccessToCode } = useUserStore();
  const { showPermissionAlert, PermissionAlertModal } =
    usePermissionAlert("clients");

  const actionMenuOptions = [
    {
      icon: <ChatBubbleOutlineIcon />,
      value: t("client_profile.manage_support_staff"),
      onClick: userHasAccessToCode("PARTICIPANT_MANAGE_SUPPORT_STAFF")
        ? handleOpenSupportStaffModal
        : showPermissionAlert,
    },
    {
      icon: <PoundInCircleIcon />,
      value: t("client_profile.manual_reimbursement"),
      onClick: userHasAccessToCode("PARTICIPANT_REWARD_MANUAL_ENTER")
        ? handleOpenManualReimbursementModal
        : showPermissionAlert,
    },
    {
      icon: <CancelOutlinedIcon />,
      value: "Disenroll Client",
      onClick: userHasAccessToCode("PARTICIPANT_DISMISS_CLIENT")
        ? handleOpenDismissModal
        : showPermissionAlert,
      sx: {
        color: theme.palette.error.main,
      },
    },
  ];

  return (
    <>
      <ActionMenu
        options={actionMenuOptions}
        id="profile-menu"
        data-testid="profile-menu"
        isSquare
        squareSx={{
          color: theme.palette.icon.dark,
          backgroundColor: theme.palette.menu.light2,
          "&:hover": {
            backgroundColor: theme.palette.surface.main,
          },
          borderRadius: "8px",
          width: "40px",
          height: "40px",
        }}
      />

      <PermissionAlertModal />
    </>
  );
};

function PoundInCircleIcon() {
  return (
    <Box position="relative" display="inline-flex">
      <CircleOutlinedIcon sx={{ fontSize: 24, color: "#6A6F81" }} />
      <CurrencyPoundIcon
        sx={{
          fontSize: 12,
          color: "#6A6F81",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          borderRadius: "50%",
        }}
      />
    </Box>
  );
}

export default ProfileMenu;
